import PropTypes from 'prop-types'
import { useCallback, useMemo, useState } from 'react'

import { Button, Layout, Tag } from '@loadsmart/miranda-react'

import { Cargo } from 'legacy/components/LoadOffer/Cargo'
import { OfferTypeBadges } from 'legacy/components/LoadOffer/OfferTypeBadges'
import { Price } from 'legacy/components/LoadOffer/Price'
import { StrikeThroughPrice } from 'legacy/components/LoadOffer/StrikeThroughPrice'
import { Tooltip } from 'legacy/components/Tooltip/Tooltip'
import { getLoadOfferPriceBreakdown } from 'legacy/utils/getLoadOfferPriceBreakdown'
import { getLoadOfferType, OFFER_TYPES } from 'legacy/utils/getLoadOfferType'

import { hasBidPriceChanged } from 'loads/domain/Bid'
import { BidButton } from 'loads/ui/offer-card/buttons/BidButton'
import { OfferChatButton } from 'offers/ui/OfferChatButton'
import { RateBreakdown } from 'offers/ui/RateBreakdown'

import { isPalletized } from '../utils/loadUtils'
import { ExpirationInfo } from './ExpirationInfo'
import { LoadRequirements } from './LoadRequirements'
import { LoadStop } from './LoadStop'
import {
  BookButtonDesktop,
  BottomSectionDesktop,
  CardColumnsContainerDesktop,
  CardDesktop,
  DetailsDesktop,
  EquipmentDesktop,
  LeftColumnDesktop,
  OfferActionsDesktop,
  PalletizedTag,
  RightColumnDesktop,
  RouteDesktop,
  RowBreakDesktop,
  StyledIconArrowRight,
  TopSectionDesktop,
} from './OfferCardDesktop.styles'
import { OfferCardFeedback } from './OfferCardFeedback'
import { OfferedBy } from './OfferedBy'

function OfferCardDesktop(props) {
  const {
    activeBid,
    analyticsContext,
    awardedBid,
    context,
    countdown,
    showBidCountdown,
    handleBid,
    handleBook,
    handleRejectLoad,
    hasActiveDNU,
    isBidDisabled,
    isCarrierReady,
    isPublicRelatedLoad,
    loadOffer,
    offeredBy,
    primaryActionButtonProps,
    requirements,
    showFeedback,
    showOnlyTwoStops,
    showPTL,
    displayTooltip,
  } = props

  const [isExpired, setExpired] = useState(false)

  const { price, daimler_vip, created_at, expires_at, biddable } = loadOffer
  const { commodity, dimensions, mileage, deadhead, ref_number, stops, temperature, weight, mode } =
    loadOffer.load
  const equipment_type = loadOffer.load.equipment_type

  const offerType = getLoadOfferType(loadOffer)
  const isContracted = offerType === OFFER_TYPES.CONTRACTED
  const isLoadCandidate = offerType === OFFER_TYPES.LOAD_CANDIDATE
  const showStrikeThroughPrice = hasBidPriceChanged(awardedBid)
  const { haul, fuel } = getLoadOfferPriceBreakdown(loadOffer)

  const renderedStops = useMemo(() => {
    if (stops.length <= 0) {
      return null
    }

    const elements = []
    const isMultiStop = stops.length > 2

    for (let i = 0; i < stops.length; i++) {
      const stop = stops[i]

      const isFirst = i === 0
      const isLast = i === stops.length - 1

      const appendArrow = !isLast
      const prependBreak = !isFirst && (i + 1) % 3 === 0

      if (prependBreak) {
        elements.push(<RowBreakDesktop key={`break-${stop.window_start}_${i}`} />)
      }

      if (!showOnlyTwoStops || isFirst || isLast) {
        elements.push(
          <LoadStop
            stop={stop}
            isPickup={isFirst}
            isDelivery={isLast}
            isVip={daimler_vip}
            isLoadCandidate={isLoadCandidate}
            isMultiStop={isMultiStop}
            key={i}
            mileage={mileage}
            deadhead={deadhead}
          />
        )
      }

      if (appendArrow) {
        elements.push(<StyledIconArrowRight key={`arrow-${stop.window_start}_${i}`} />)
      }
    }
    return elements
  }, [stops, deadhead, showOnlyTwoStops, daimler_vip, isLoadCandidate, mileage])

  const handleCardClick = useCallback(
    event => {
      if (isExpired) {
        event.preventDefault()
        event.stopPropagation()
      }
    },
    [isExpired]
  )

  const hasRequirements = requirements.length > 0

  const isCarrierReadyEnabled = !isCarrierReady

  return (
    <CardDesktop isVip={daimler_vip} onClick={handleCardClick} isExpired={isExpired}>
      <CardColumnsContainerDesktop>
        <LeftColumnDesktop>
          <RouteDesktop>
            <OfferTypeBadges
              isVip={daimler_vip}
              isContracted={isContracted}
              isLoadCandidate={isLoadCandidate}
            />
            {renderedStops}
          </RouteDesktop>
          <DetailsDesktop>
            <EquipmentDesktop
              equipmentType={equipment_type}
              mode={showPTL ? mode : null}
              temperature={temperature}
              showLabel
              showMode
            />
            {isPalletized(loadOffer.load) && (
              <PalletizedTag variant="success" size="small">
                Palletized
              </PalletizedTag>
            )}
            <Cargo
              weight={weight}
              commodity={commodity}
              dimensions={dimensions}
              truncate
              boldWeight
            />
          </DetailsDesktop>
        </LeftColumnDesktop>
        <RightColumnDesktop>
          <TopSectionDesktop>
            {hasRequirements && <LoadRequirements requirements={requirements} />}
            {!loadOffer.load.all_appointments_confirmed && (
              <Tag variant="danger">APPOINTMENTS PENDING</Tag>
            )}
          </TopSectionDesktop>
          <BottomSectionDesktop>
            <Layout.Stack gap="spacing-0-5" align="flex-end">
              {showStrikeThroughPrice && <StrikeThroughPrice price={awardedBid.bookPrice} />}
              <Price price={awardedBid?.price ?? price} />
              <RateBreakdown haulPrice={haul} fuelPrice={fuel} />
            </Layout.Stack>
            {!isPublicRelatedLoad && (
              <OfferActionsDesktop>
                <OfferChatButton
                  stops={stops}
                  refNumber={ref_number}
                  isLoadCandidate={isLoadCandidate}
                  isContracted={isContracted}
                  context={context}
                  analyticsContext={analyticsContext}
                />
                {isCarrierReadyEnabled || isPublicRelatedLoad ? (
                  <Tooltip>
                    <BookButtonDesktop
                      disabled
                      data-testid="OfferCardDesktop__NotEligibleBookButton"
                    >
                      {primaryActionButtonProps.text}
                    </BookButtonDesktop>

                    <Tooltip.Bubble width={256} forceRightOffset>
                      {isCarrierReadyEnabled &&
                        `You won’t be able to book loads until your documents are approved`}
                      {isPublicRelatedLoad && `You need to sign in to be able to book loads`}
                    </Tooltip.Bubble>
                  </Tooltip>
                ) : !isExpired ? (
                  <>
                    {isContracted && (
                      <Button onClick={handleRejectLoad} variant="warning">
                        Reject
                      </Button>
                    )}
                    {biddable && (
                      <BidButton
                        displayCountdown={showBidCountdown}
                        displayTooltip={displayTooltip}
                        onClick={handleBid}
                        price={Boolean(activeBid) && activeBid.price}
                        disabled={isBidDisabled}
                        countdown={countdown}
                      />
                    )}
                    <Button
                      title={primaryActionButtonProps.text}
                      type="button"
                      variant="primary"
                      onClick={handleBook}
                      disabled={hasActiveDNU}
                      {...primaryActionButtonProps}
                    />
                  </>
                ) : (
                  <BookButtonDesktop disabled>Expired</BookButtonDesktop>
                )}
              </OfferActionsDesktop>
            )}
          </BottomSectionDesktop>
        </RightColumnDesktop>
      </CardColumnsContainerDesktop>

      <OfferedBy name={offeredBy} />

      {(isContracted || isLoadCandidate) && (
        <ExpirationInfo
          isContracted={isContracted}
          isLoadCandidate={isLoadCandidate}
          created_at={new Date(created_at)}
          expires_at={new Date(expires_at)}
          onExpiration={setExpired}
        />
      )}
      {showFeedback && <OfferCardFeedback uuid={loadOffer.uuid} />}
    </CardDesktop>
  )
}

OfferCardDesktop.propTypes = {
  activeBid: PropTypes.shape(),
  analyticsContext: PropTypes.string.isRequired,
  context: PropTypes.string.isRequired,
  countdown: PropTypes.string.isRequired,
  showBidCountdown: PropTypes.bool.isRequired,
  handleBid: PropTypes.func.isRequired,
  handleBook: PropTypes.func.isRequired,
  hasActiveDNU: PropTypes.bool.isRequired,
  isBidDisabled: PropTypes.bool.isRequired,
  isCarrierReady: PropTypes.bool,
  isPublicRelatedLoad: PropTypes.bool,
  loadOffer: PropTypes.shape().isRequired,
  requirements: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      params: PropTypes.shape({}),
    })
  ).isRequired,
  showOnlyTwoStops: PropTypes.bool.isRequired,
  showFeedback: PropTypes.bool.isRequired,
  offeredBy: PropTypes.string,
  awardedBid: PropTypes.shape({
    bookPrice: PropTypes.string,
    price: PropTypes.string,
  }),
  primaryActionButtonProps: PropTypes.shape({
    children: PropTypes.string,
  }),
  showPTL: PropTypes.bool,
}

OfferCardDesktop.defaultProps = {
  activeBid: undefined,
  isCarrierReady: false,
  isPublicRelatedLoad: false,
  showPTL: false,
}

export { OfferCardDesktop }
